import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch } from "@/components/ui";
import { actionsKeys, getRequiredFields, permissionActions, WEIGHING_FACTORS } from "@/lib/utils";
import { setMetricData } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useDownloadExcelMetric, useGetDistributionRuleByCommunityYear, useUpdateRuleStatusMutation, useUploadExcelMetricsByRuleIdMutation, useUploadExcelMetricsMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Edit, Eye, MoreVertical } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "@/components/Toast";
import { BUDGET_TYPE, COMMUNITY_ID, RULE_ID, RULE_NAME, UPLOAD_FILE, YEAR } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { routes } from "@/routes/utils";
import moment from "moment";

const useWeighingFactor = ({activeType}: {activeType: any}) => {
  const { type, glId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = useRef<boolean>(false);
  const form = useForm<formValues>({
    defaultValues: {
      [RULE_ID]: "",
      [RULE_NAME]: "",
      [UPLOAD_FILE]: null
    },
    resolver: zodResolver(schema),
  });
  const location = useLocation();
  const isConfig = location.pathname?.includes(routes.distributionRuleConfiguration);
  const fromTraceability = location?.pathname?.includes(routes.traceability);

  const currentType = activeType || type;
  
  const requiredFields = getRequiredFields(schema);

  const { communityData, currentFinanceYear, accessPermission } = useSelector(
    (state: RootState) => state.currentData
  );
  
  const communityName = communityData?.communityName || communityData?.name || "";
  
  const hasCreatePermission = useMemo(() => {
    return accessPermission?.[actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase()]?.[permissionActions.CREATE]
  }, [accessPermission]);

  const hasUpdatePermission = useMemo(() => {
    return accessPermission?.[actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase()]?.[permissionActions.UPDATE]
  }, [accessPermission]);
  
  const orgBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionRuleConfiguration}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: "",
        name: <span className="text-primary">Weighing Factor</span>,
      },
    ],
    [communityName]
  );

  const traceabilityBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}`,
        name: `Traceability`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}/${routes.distributionRuleConfiguration}/${glId}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: "",
        name: <span className="text-primary">Weighing Factor</span>,
      },
    ],
    [communityName]
  );

  const breadcrumbData = fromTraceability ? traceabilityBreadcrumbData : orgBreadcrumbData;

  const {data: communityRules, isLoading: isLoadingRules, refetch: refetchCommunityRules, isRefetching: isRefetchRules } = useGetDistributionRuleByCommunityYear({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear)
    }
  });

  useEffect(() => {
    if (currentType) {
      refetchCommunityRules();
    }
  }, [currentType]);

  const tableData = useMemo(() => {
    return communityRules?.data?.filter((data: any) => data?.distributionMethod == WEIGHING_FACTORS && data?.budgetType == currentType);
  }, [communityRules])
  
  const { data: downloadTemplateData, isRefetching: isDownloadTemplateLoading, refetch: refetchDownloadTemplate } = useDownloadExcelMetric({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: false,
    }
  });

  useEffect(() => {
    if (downloadTemplateData) {
      const blob = new Blob([downloadTemplateData.data], {
        type: downloadTemplateData.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Try to get filename from headers, fallback to fileName or default
      const contentDisposition = downloadTemplateData.headers["content-disposition"];
      let downloadFileName = "metics.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          downloadFileName = match[1];
        }
      }

      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      queryClient.setQueryData(["download-excel-distribution-metric"], null);
    }
  }, [downloadTemplateData])
  
  const onDownloadTemplate = () => {
    refetchDownloadTemplate();
  };
  
  const { mutate: uploadTemplate, isPending: isUploadTemplateLoading } =
    useUploadExcelMetricsMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenUploadDialog(false);
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`Template Uploaded Successfully`);
      },
    });

  const { mutate: updateTemplate, isPending: isUpdatePending } =
    useUploadExcelMetricsByRuleIdMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenUploadDialog(false);
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`Template Uploaded Successfully`);
      },
    });

  const { mutate: updateRuleStatus, isPending: isUpdateStatusPending } =
    useUpdateRuleStatusMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenUploadDialog(false);
        queryClient.invalidateQueries({queryKey: ["distribution-rule-community-year", communityData?.communityId, currentFinanceYear]})
        toast.success(`Status Updated Successfully`);
      },
    });
    
  const onUploadTemplateClick = () => {
    isEdit.current = false;
    setOpenUploadDialog(true);
  };

  const onUploadTemplate = (data: formValues) => {
    if (!data[UPLOAD_FILE]) {
      toast.error("Please select file");
      return;
    }

    const obj = {
      ...data,
      [COMMUNITY_ID]: communityData?.communityId,
      [YEAR]: currentFinanceYear,
      [BUDGET_TYPE]: currentType
    }

    delete obj[RULE_ID];

    if (isEdit.current) {
      updateTemplate({
        communityId: communityData?.communityId,
        ruleId: data[RULE_ID] as string,
        data: obj
      });
    } else {
      uploadTemplate({ 
        data: obj
      });
    }
  };

  const columns = [
    {
      accessorKey: "ruleName",
      header: <div>Rule Name</div>,
    },
    {
      accessorKey: "createdAt",
      header: <div>Uploaded On</div>,
      accessorFn: (row: any) => row?.createdAt ? moment(row?.createdAt).format('MM/DD/YYYY') : "" 
    },
    {
      accessorKey: "active",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        return (
          <Switch
            id="active"
            onCheckedChange={() => {
              updateRuleStatus({ruleId: row?.original?.[RULE_ID], communityId: communityData?.communityId});
            }}
            checked={row?.original?.active}
            disabled={isUpdateStatusPending || isConfig || !hasUpdatePermission}
          />
        )
      }
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({row}: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch(setMetricData({ruleName: row?.original?.ruleName}))
                  if (isConfig) {
                    navigate(`${row?.original?.distributionRuleId}/details`)
                  } else {
                    navigate(`${routes.weighingFactor}/${row?.original?.distributionRuleId}/view`)
                  }
                }}
              >
                <Eye className="w-4 h-4" />
                View
              </DropdownMenuItem>
              {isConfig || !hasUpdatePermission ? <></> : <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.setValue(RULE_ID, row?.original?.[RULE_ID]);
                  form.setValue(RULE_NAME, row?.original?.[RULE_NAME]);
                  isEdit.current = true;
                  setOpenUploadDialog(true);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return {
    onUploadTemplateClick,
    onUploadTemplate,
    openUploadDialog,
    fileInputRef,
    onDownloadTemplate,
    setOpenUploadDialog,
    isUploadTemplateLoading,
    isDownloadTemplateLoading,
    isLoadingRules,
    tableData,
    columns,
    form,
    requiredFields,
    isUpdatePending,
    isEdit: isEdit.current,
    isRefetchRules,
    isConfig,
    breadcrumbData,
    currentType,
    hasCreatePermission
  }
}

export default useWeighingFactor;